var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { AccountEndpoints, } from './types/accounts';
import { AgreementsEndpoints, } from './types/agreements';
import { ApplicationsEndpoints, } from './types/applications';
import { CardEndpoints, } from './types/cards';
import { NotificationsEndpoints, } from './types/notifications';
import { RewardsEndpoints, } from './types/rewards';
import { TransactionsEndpoints, } from './types/transactions';
import { UserEndpoints, } from './types/user';
import { VGSEndpoints, } from './types/vgs';
import { ApiClientBase } from './apiClientBase';
/**
 * `ApiClient` is a encapsulation of the network request logic needed to interact with the backend
 * as an authenticated user. It's intended to be as stateless as possible to simplify testing (it
 * only receives a reference to an `AxiosInstance`, which we can mock out later to create a
 * `MockApiClient` for testing).
 */
class ApiClient extends ApiClientBase {
    /**
     * ====================================================
     * ACCOUNT ENDPOINTS
     * ====================================================
     */
    addBankAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.AddBankAccount, args);
            return resp.data;
        });
    }
    addPendingBankAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.AddPendingBankAccount, args);
            return resp.data;
        });
    }
    cancelPayment(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(AccountEndpoints.CancelPayment, args);
        });
    }
    createPresignUrl(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.CreatePresignUrl, args);
            return resp.data;
        });
    }
    deleteBankAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(AccountEndpoints.DeleteBankAccount, args);
        });
    }
    deletePendingBankAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(AccountEndpoints.DeletePendingBankAccount, args);
        });
    }
    getAccountSummaries(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetAccountSummaries, args);
            return resp.data;
        });
    }
    getProductChangeOffers(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetProductChangeOffers, args);
            return resp.data;
        });
    }
    getProductChangeHistory(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetProductChangeHistory, args);
            return resp.data;
        });
    }
    getProductChangePreview(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetProductChangePreview, args);
            return resp.data;
        });
    }
    initiateProductChange(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.InitiateProductChange, args);
            return resp.data;
        });
    }
    getProductChangeRevertPreview(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetProductChangeRevertPreview, args);
            return resp.data;
        });
    }
    revertProductChange(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.RevertProductChange, args);
            return resp.data;
        });
    }
    adminSendProductChangeDowngradeEmail(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.AdminSendProductChangeDowngradeEmail, args);
            return resp.data;
        });
    }
    getBankAccounts(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetBankAccounts, args);
            return resp.data;
        });
    }
    /**
     * Every bank-linking attempt on an account, successes and failures alike —
     * the trail CX reads to diagnose why a cardholder couldn't link a bank.
     * Account-scoped, unlike {@link getBankAccounts}, which is user-scoped.
     */
    getBankAccountLinkingAttempts(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetBankAccountLinkingAttempts, args);
            return resp.data;
        });
    }
    createBankAccountViewToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.CreateBankAccountViewToken, args);
            return resp.data;
        });
    }
    getBankAccountInfoByToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetBankAccountInfoByToken, args);
            return resp.data;
        });
    }
    getUserFromAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetUserFromAccount, args);
            return resp.data;
        });
    }
    getPrimaryAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetPrimaryAccount, args);
            return resp.data;
        });
    }
    getAccountByProcessorAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetAccountByProcessorAccount, args);
            return resp.data;
        });
    }
    getFileStatus(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.get(`${AccountEndpoints.GetFileStatusPrefix}/${args.fileUuid}`);
            return resp.data;
        });
    }
    getPlaidLinkToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetPlaidLinkToken, args);
            return resp.data;
        });
    }
    getScheduledPaymentsByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetScheduledPaymentsByAccount, args);
            return resp.data;
        });
    }
    getScheduledPaymentsByUser(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetScheduledPaymentsByUser, args);
            return resp.data;
        });
    }
    getStatements(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetStatements, args);
            return resp.data;
        });
    }
    getStatementUrl(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetStatementUrl, args);
            return resp.data;
        });
    }
    /** TODO: remove these two and their corresponding request/response */
    exportTransactions(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(TransactionsEndpoints.ExportTransactions, args);
            return resp.data;
        });
    }
    createDispute(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(TransactionsEndpoints.CreateDispute, args);
            return resp.data;
        });
    }
    getTransactionsExportedUrl(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(TransactionsEndpoints.GetTransactionsExportedURL, args);
            return resp.data;
        });
    }
    getExportedStatementUrl(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.GetExportedStatementUrl, args);
            return resp.data;
        });
    }
    initiateAccountTransfer(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.InitiateAccountTransfer, args);
            return resp.data;
        });
    }
    schedulePayment(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AccountEndpoints.SchedulePayment, args);
            return resp.data;
        });
    }
    updateScheduledPayment(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalArgs = Object.assign({}, args);
            delete finalArgs.paymentMethodType;
            delete finalArgs.paymentMethodUuid;
            const resp = yield this.client.post(AccountEndpoints.UpdateScheduledPayment, finalArgs);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * AGREEMENTS ENDPOINTS
     * ====================================================
     */
    getPresignedAttachmentUrl(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AgreementsEndpoints.GetPresignedAttachmentUrl, args);
            return resp.data;
        });
    }
    acknowledgeAgreement(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(AgreementsEndpoints.AcknowledgeAgreement, args);
        });
    }
    getAgreementsToAcknowledge(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AgreementsEndpoints.GetAgreementsToAcknowledge, args);
            return resp.data;
        });
    }
    getApplicationAgreements(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AgreementsEndpoints.GetApplicationAgreements, args);
            return resp.data;
        });
    }
    getStaticDisclosures() {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(AgreementsEndpoints.GetStaticDisclosures);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * APPLICATIONS ENDPOINTS
     * ====================================================
     */
    checkWaitlist(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.CheckWaitlist, args);
            return resp.data;
        });
    }
    deleteApplication(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(ApplicationsEndpoints.DeleteApplication, args);
        });
    }
    getApplicationStatus(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetApplicationStatus, args);
            return resp.data;
        });
    }
    getApprovalOffers(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetApprovalOffers, args);
            return resp.data;
        });
    }
    getPlaidIdentityLinkToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetPlaidIdentityLinkToken, args);
            return resp.data;
        });
    }
    getIdentityVerificationToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetIdentityVerificationToken, args);
            return resp.data;
        });
    }
    prefillPartnerApplication(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.PrefillPartnerApplication, args);
            return resp.data;
        });
    }
    getProductLaunchStatus(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetProductLaunchStatus, args);
            return resp.data;
        });
    }
    manageOffer(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.ManageOffer, args);
            return resp.data;
        });
    }
    ssn4ToSsn9(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.Ssn4ToSsn9, args);
            return resp.data;
        });
    }
    shouldVerifyPhone(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.ShouldVerifyPhone, args);
            return resp.data;
        });
    }
    submitApplication(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.SubmitApplication, args, { baseURL: this.vgsBaseUrl });
            return resp.data;
        });
    }
    submitIdentityVerification(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.SubmitIdentityVerification, args);
            return resp.data;
        });
    }
    verifyPrescreenedIdentity(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.VerifyPrescreenedIdentity, args);
            return resp.data;
        });
    }
    lookupApplications(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.LookupApplications, args);
            return resp.data;
        });
    }
    getApplicationByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetApplicationByAccount, args);
            return resp.data;
        });
    }
    getApplicationByUser(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetApplicationByUser, args);
            return resp.data;
        });
    }
    getApplicationByUuid(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetApplicationByUuid, args);
            return resp.data;
        });
    }
    getApplicationByReferenceNumber(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(ApplicationsEndpoints.GetApplicationByReferenceNumber, args);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * CARD ENDPOINTS
     * ====================================================
     */
    activateCard(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(CardEndpoints.ActivateCard, args);
        });
    }
    activateCardLoggedIn(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(CardEndpoints.ActivateCardLoggedIn, args);
        });
    }
    createPaymentAuthCode(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(CardEndpoints.CreatePaymentAuthCode, args);
            return resp.data;
        });
    }
    createViewCardToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(CardEndpoints.CreateViewCardToken, args);
            return resp.data;
        });
    }
    getCardSummaries(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(CardEndpoints.GetCardSummaries, args);
            return resp.data;
        });
    }
    changeCardPin(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.client.post(CardEndpoints.ChangeCardPin, args, { baseURL: this.vgsBaseUrl });
            return res.data;
        });
    }
    getPushProvisioningData(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(CardEndpoints.GetPushProvisioningData, args);
            return resp.data;
        });
    }
    replaceCard(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(CardEndpoints.ReplaceCard, args);
            return resp.data;
        });
    }
    updateCardStatus(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(CardEndpoints.UpdateCardStatus, args);
        });
    }
    /**
     * ====================================================
     * NOTIFICATIONS ENDPOINTS
     * ====================================================
     */
    togglePushNotifications(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(NotificationsEndpoints.TogglePushNotifications, args);
        });
    }
    /**
     * ====================================================
     * PRODUCTS ENDPOINTS
     * ====================================================
     */
    getProductFeatures(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.get(`${AccountEndpoints.GetProductFeaturesPrefix}/${args.productSlug}/features`);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * REWARDS ENDPOINTS
     * ====================================================
     */
    checkIfUserExistsOnPartner(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.CheckIfUserExistsOnPartner, args);
            return resp.data;
        });
    }
    claimBenefit(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.ClaimBenefit, args);
            return resp.data;
        });
    }
    completeExternalOauthLogin(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.CompleteExternalOauthLogin, args);
            return resp.data;
        });
    }
    getBenefitsByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetBenefitsByAccount, args);
            return resp.data;
        });
    }
    getBenefitDetails(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetBenefitDetails, args);
            return resp.data;
        });
    }
    getEarningRatesByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetEarningRatesByAccount, args);
            return resp.data;
        });
    }
    getEligibleCharitiesForRedemption(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetEligibleCharitiesForRedemption, args);
            return resp.data;
        });
    }
    getExternalLoginUrl(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetExternalLoginUrl, args);
            return resp.data;
        });
    }
    getGiftCardCatalog(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetGiftCardCatalog, args);
            return resp.data;
        });
    }
    getProductMarketingVariant(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetProductMarketingVariant, args);
            return resp.data;
        });
    }
    getProductMarketingVariantByBrand(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetProductMarketingVariantByBrand, args);
            return resp.data;
        });
    }
    getPurchasedGiftCardForAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetPurchasedGiftCardForAccount, args);
            return resp.data;
        });
    }
    getPurchasedGiftCardsForAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetPurchasedGiftCardsForAccount, args);
            return resp.data;
        });
    }
    getHistoricalGoalsByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetHistoricalGoalsByAccount, args);
            return resp.data;
        });
    }
    getHistoricalGoalsByUser(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetHistoricalGoalsByUser, args);
            return resp.data;
        });
    }
    getPointsEntriesByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetPointsEntriesByAccount, args);
            return resp.data;
        });
    }
    getPointsEntriesByEvent(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetPointsEntriesByEvent, args);
            return resp.data;
        });
    }
    getPointsMultiplierDetails(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetPointsMultiplierDetails, args);
            return resp.data;
        });
    }
    getRedemptionRatesByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetRedemptionRatesByAccount, args);
            return resp.data;
        });
    }
    getReferralInfoByAccount(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetReferralInfoByAccount, args);
            return resp.data;
        });
    }
    getTransferRatesForRedemption(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.GetTransferRatesForRedemption, args);
            return resp.data;
        });
    }
    purchaseGiftCardFromCatalog(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.PurchaseGiftCardFromCatalog, args);
            return resp.data;
        });
    }
    redeemPointsForCharity(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.RedeemPointsForCharity, args);
            return resp.data;
        });
    }
    redeemPointsForStatementCredit(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(RewardsEndpoints.RedeemPointsForStatementCredit, args);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * TRANSACTIONS ENDPOINTS
     * ====================================================
     */
    getNotificationSettings(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(TransactionsEndpoints.GetNotificationSettings, args);
            return resp.data;
        });
    }
    getTransactionById(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(TransactionsEndpoints.GetTransactionById, args);
            return resp.data;
        });
    }
    getTransactions(_a) {
        return __awaiter(this, void 0, void 0, function* () {
            var { pageSize = 20 } = _a, otherArgs = __rest(_a, ["pageSize"]);
            const resp = yield this.client.post(TransactionsEndpoints.GetTransactions, Object.assign(Object.assign({}, otherArgs), { pageSize }));
            return resp.data;
        });
    }
    updateNotificationSettings(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(TransactionsEndpoints.UpdateNotificationSettings, args);
        });
    }
    listDisputes(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(TransactionsEndpoints.ListDisputes, args);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * USER ENDPOINTS
     * ====================================================
     */
    createPasswordFromToken(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.CreatePasswordFromToken, args);
            return resp.data;
        });
    }
    forgotPassword(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(UserEndpoints.ForgotPassword, args);
        });
    }
    getUser(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.GetUser, args);
            return resp.data;
        });
    }
    getPartnerUserState(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.GetPartnerUserState, args);
            return resp.data;
        });
    }
    logout(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(UserEndpoints.Logout, args);
        });
    }
    preVerifyBirthdate(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.PreVerifyBirthdate, args);
            return resp.data;
        });
    }
    preVerifyCreatePassword(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.PreVerifyCreatePassword, args);
            return resp.data;
        });
    }
    preVerifyMfaCode(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.PreVerifyMfaCode, args);
            return resp.data;
        });
    }
    resetPassword(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.post(UserEndpoints.ResetPassword, args);
        });
    }
    getAuthCode(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.GetAuthCode, args);
            return resp.data;
        });
    }
    sendEmailVerification(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.SendEmailVerification, args);
            return resp.data;
        });
    }
    sendMfaCode(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(UserEndpoints.SendMfaCode, args);
            return resp.data;
        });
    }
    /**
     * ====================================================
     * VGS ENDPOINTS
     * ====================================================
     */
    getSensitiveCardInfo(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(VGSEndpoints.GetSensitiveCardInfo, args, { baseURL: this.vgsBaseUrl });
            return resp.data;
        });
    }
    getSensitiveCardInfoAfterApproval(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.client.post(VGSEndpoints.GetSensitiveCardInfoAfterApproval, args, { baseURL: this.vgsBaseUrl });
            return resp.data;
        });
    }
}
export { ApiClient };
